import React, { useCallback, useEffect, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { DefinitionTooltip } from '@carbon/react';
import { DownloadButton } from 'components/download-button/download-button';
import type { ExportParams } from 'lib/hooks/useServerExport';
import { toastAtom, TypeCurveHeadersQueryParams } from './atoms';
import { useInventoryListingExport } from './hooks';

interface ExportInventoryListingButtonProps {
  queryParams: TypeCurveHeadersQueryParams | undefined;
  /** WORKSPACES matching the filters — the listing's own totalCount. */
  totalCount: number;
  disabled?: boolean;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
}

/**
 * CSV export of the Inventories listing.
 *
 * A sibling of the Type Curves export rather than a shared component: it posts
 * to its own endpoint and listens on its own Pusher channel, because the file
 * is zone-grained — one line per `(workspace, area, formation, type curve)`,
 * flattened exactly as the table is.
 *
 * @param {ExportInventoryListingButtonProps} props - Filters, count and column state.
 * @return {JSX.Element} The download button.
 */
export const ExportInventoryListingButton: React.FC<
  ExportInventoryListingButtonProps
> = ({ queryParams, totalCount, disabled, columnVisibility, columnOrder }) => {
  const setToast = useSetAtom(toastAtom);
  const { startExport, isExporting, exportStatus } =
    useInventoryListingExport();

  useEffect(() => {
    if (exportStatus.status === 'complete') {
      setToast({
        kind: 'success',
        title: 'Export Complete',
        subtitle: 'Your Inventory data has been exported',
        timeout: 5000,
      });
    } else if (exportStatus.status === 'failed') {
      setToast({
        kind: 'error',
        title: 'Export Failed',
        subtitle: exportStatus.error || 'Failed to export Inventory data',
        timeout: 5000,
      });
    }
  }, [exportStatus.status, exportStatus.error, setToast]);

  const getVisibleColumnsInOrder = useCallback(() => {
    return columnOrder.filter((colId) => columnVisibility[colId] !== false);
  }, [columnOrder, columnVisibility]);

  const visibleColumnsCount = useMemo(() => {
    return getVisibleColumnsInOrder().length;
  }, [getVisibleColumnsInOrder]);

  const handleExport = useCallback(() => {
    if (isExporting || !queryParams || visibleColumnsCount === 0) return;

    startExport({
      queryParams,
      visibleColumns: getVisibleColumnsInOrder(),
    } as ExportParams);
  }, [
    isExporting,
    queryParams,
    visibleColumnsCount,
    getVisibleColumnsInOrder,
    startExport,
  ]);

  const isDisabled =
    disabled ||
    totalCount === 0 ||
    isExporting ||
    !queryParams ||
    visibleColumnsCount === 0;

  const getDisabledReason = (): string | null => {
    if (isExporting) return null;
    if (!queryParams) return 'Loading data...';
    if (totalCount === 0) return 'No inventory matches the current filters';
    if (visibleColumnsCount === 0) return 'No columns are visible to export';
    if (disabled) return 'Export is currently unavailable';
    return null;
  };

  const disabledReason = getDisabledReason();

  const button = (
    <DownloadButton
      loading={isExporting}
      loadingText="Exporting..."
      onClick={handleExport}
      disabled={isDisabled}
      kind="secondary"
    >
      Export Filtered Inventory
    </DownloadButton>
  );

  if (isDisabled && disabledReason) {
    return (
      <DefinitionTooltip align="bottom-end" definition={disabledReason}>
        {button}
      </DefinitionTooltip>
    );
  }

  return button;
};
